import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _this = this;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * @class entityActionFactory
 */
import _ from 'underscore';
import moment from 'moment';
import * as ajax from '../ajax';
import selectActionFactory from './selectActionFactory';
import tokenErrorActions from '../tokenErrorActionCreators';
import { constantsRegistry } from '../../constants/tableConstantsFactory';
import store from '../../reducers/storeRegistration';
import optimizeIndexQueryFilters from '../../utils/optimizeIndexQueryFilters';
import fetchCabinetIndexCount from '../cabinetIndexCountThunks';

/**
 *  @param entity the entity name: e.g. "report"
 *  @param entityRoute the base route to get to the entity: e.g. "reports"
 */
const entityActionFactory = (entity, entityRoute) => {
  const selectActions = selectActionFactory(entity, entityRoute);

  // if these constants have any custom values, they should be imported
  // before getting to this point.
  const constants = constantsRegistry(entity);

  // Queue to store the latest pending query when a request is in progress.
  // This ensures the final user input is always processed even during rapid typing.
  let pendingQueryQueue = null;
  const filterClear = () => ({
    type: constants.FILTER_CLEAR
  });
  const index = indexQuery => ({
    type: constants.INDEX,
    indexQuery
  });
  const indexCancel = () => ({
    type: constants.INDEX_CANCEL
  });
  const indexFail = function indexFail(data) {
    let query = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return {
      type: constants.INDEX_FAIL,
      query,
      data
    };
  };
  const indexSuccess = function indexSuccess(data) {
    let query = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return {
      type: constants.INDEX_SUCCESS,
      data,
      query: _objectSpread(_objectSpread({}, query), {}, {
        initialized: true
      })
    };
  };

  //* ***********  SERVER INTERACTION METHODS  *******************
  const optimizeFilters = optimizeIndexQueryFilters;

  /**
   *  Query the server based on information passed via incomingQuery
   */
  const _serverIndex = function serverIndex(incomingQuery) {
    let onlyOnDemand = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    let isRetry = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    return async dispatch => {
      var _cleanedQuery$filters;
      if (!incomingQuery) throw new Error('query is now required');
      const query = JSON.parse(JSON.stringify(incomingQuery));
      // check for pending filters, which have been applied between the last index run
      if (query.pendingFilters) {
        query.filters = _objectSpread(_objectSpread({}, query.filters), query.pendingFilters);
      }

      // if query is same as last time, the query hasn't expired, and onlyOnDemand === true, don't
      // requery. Also don't publish index success which avoids the need in components to compare
      // props ideally wouldn't use state, but old query via props would complicate serverIndex calls
      const state = store.getState()[entity];
      if (query.initialized && _.isEqual(query, state.indexQuery) && moment().isBefore(moment.unix(query.expiration), 'minute') // use minute precision for tests
      && onlyOnDemand && !isRetry) {
        return null;
      }

      // avoid repeated requests when the initial request is still loading
      // Instead of dropping the request, queue it so it will be processed
      // when current request completes
      if (state.loading && !isRetry) {
        pendingQueryQueue = {
          query: incomingQuery,
          onlyOnDemand
        };
        return null;
      }
      const url = "/".concat(entityRoute);
      // sanitize the query
      const cleanedQuery = _objectSpread(_objectSpread({}, query), {}, {
        filters: optimizeFilters(query.filters)
      });
      delete cleanedQuery.expiration;
      delete cleanedQuery.initialized;

      // forms index requires formTypeIdentifiers; skip request without dispatching (silent no-op)
      if (entityRoute === 'forms' && !((_cleanedQuery$filters = cleanedQuery.filters) !== null && _cleanedQuery$filters !== void 0 && (_cleanedQuery$filters = _cleanedQuery$filters.formTypeIdentifiers) !== null && _cleanedQuery$filters !== void 0 && _cleanedQuery$filters.length)) {
        return null;
      }
      dispatch(index(incomingQuery));
      try {
        const response = await ajax.get(url, cleanedQuery);
        // console.timeEnd('serverIndex ' + entity);
        // add a 5 minute expiration to the query
        query.expiration = moment().add(5, 'minutes').unix();
        const result = dispatch(indexSuccess(response.data, query));
        if (entity === 'cabinet' && entityRoute === 'forms') {
          store.dispatch(fetchCabinetIndexCount(query));
        }

        // Check if there's a pending query that was queued while this request was in progress
        if (pendingQueryQueue) {
          const _pendingQueryQueue = pendingQueryQueue,
            queuedQuery = _pendingQueryQueue.query,
            queuedOnlyOnDemand = _pendingQueryQueue.onlyOnDemand;
          pendingQueryQueue = null; // Clear the queue before dispatching to avoid infinite loops
          dispatch(_serverIndex(queuedQuery, queuedOnlyOnDemand));
        }
        return result;
      } catch (e) {
        // Preserve the queued request - user's latest input should not be lost
        const queuedRequest = pendingQueryQueue;
        pendingQueryQueue = null;
        if (queuedRequest) {
          // Process the queued request instead of retrying the failed stale one
          // This ensures the user's final input (e.g., clearing the search) is honored
          return dispatch(_serverIndex(queuedRequest.query, queuedRequest.onlyOnDemand));
        }

        // Only retry the original failed request if nothing was queued
        const setIsRetry = true; // bypass internal `if` gates to successfully requery the server
        return dispatch(tokenErrorActions.handleError(e, entity, indexFail.bind(_this, e, query), _serverIndex.bind(_this, incomingQuery, onlyOnDemand, setIsRetry)));
      }
    };
  };
  const pageNext = incomingQuery => {
    if (!incomingQuery) throw new Error('incomingQuery is required');
    const query = _objectSpread(_objectSpread({}, incomingQuery), {}, {
      paging: _objectSpread(_objectSpread({}, incomingQuery.paging), {}, {
        current: incomingQuery.paging.current + 1
      })
    });
    return dispatch => dispatch(_serverIndex(query));
  };
  const pagePrevious = incomingQuery => {
    if (!incomingQuery) throw new Error('incomingQuery is required');
    const page = incomingQuery.paging.current > 0 ? incomingQuery.paging.current - 1 : 0;
    const query = _objectSpread(_objectSpread({}, incomingQuery), {}, {
      paging: _objectSpread(_objectSpread({}, incomingQuery.paging), {}, {
        current: page
      })
    });
    return dispatch => dispatch(_serverIndex(query));
  };
  const serverFilter = function serverFilter(incomingFilters, incomingQuery) {
    let onlyOnDemand = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    // new filters
    if (!incomingQuery) throw new Error('incomingQuery is required');
    let filters = incomingFilters || {};

    // check for pending filters, which have been applied between the last index run
    if (incomingQuery.pendingFilters) {
      filters = _objectSpread(_objectSpread({}, filters), incomingQuery.pendingFilters);
    }
    const query = _objectSpread(_objectSpread({}, incomingQuery), {}, {
      filters,
      // these are used for setting query filters for the next time the query is run. This allows
      // unrelated reducers to set filters in state without needing to trigger a serverIndex. Since
      // the query just completed, these should be cleared.
      pendingFilters: null
    });
    if (query.paging) {
      // reset the page
      query.paging.current = 0;
    }

    // request new data with new filter
    return dispatch => dispatch(_serverIndex(query, onlyOnDemand));
  };

  /**
   *  Apply the sort property to the query and call serverIndex
   */
  const sortBy = (sortAttribute, query) => dispatch => {
    const sortedQuery = _objectSpread({}, query);
    if (sortedQuery.sortAttribute === sortAttribute) {
      if (sortedQuery.sortDirection === 'ASC') {
        sortedQuery.sortDirection = 'DESC';
      } else if (sortedQuery.sortDirection === 'DESC') {
        sortedQuery.sortAttribute = null;
        sortedQuery.sortDirection = null;
      } else {
        sortedQuery.sortAttribute = sortAttribute;
        sortedQuery.sortDirection = 'ASC';
      }
    } else {
      sortedQuery.sortAttribute = sortAttribute;
      sortedQuery.sortDirection = 'ASC';
    }
    return dispatch(_serverIndex(sortedQuery));
  };

  // we need to return an object with all the creators defined.
  return _objectSpread({
    filterClear,
    index,
    indexCancel,
    indexSuccess,
    indexFail,
    pageNext,
    pagePrevious,
    serverFilter,
    serverIndex: _serverIndex,
    sortBy
  }, selectActions);
};
export default entityActionFactory;